import { useEffect, useSyncExternalStore } from 'react';
import { message } from 'antd';
import posthog from 'posthog-js';
import { WO_SUMMARY_FEATURE_FLAG } from '../../utils/EnvConfigUtils';
import { isSubContractedFromWorkOrder, isSubContractedWorkOrder } from '../../utils/WorkOrderUtils';
import {
	FetchResolutionKnowledge,
	ResolutionKnowledgeExtraction,
	ResolutionKnowledgeFeedback,
	ResolutionKnowledgeRecord,
	ResolutionSummaryWorkOrder,
	SaveResolutionKnowledgeFeedback,
} from '../../types/resolutionKnowledge';

/**
 * Completion Summary state, shared per work order by the card (main column) and the restore pill
 * (right column). It lives in a small module store rather than a React context so the two can sit
 * in different columns without a provider wrapping the page.
 *
 * Only three parts are specific to the Completion Summary: when it is active (`active` below),
 * how it loads (`fetchKnowledge`) and what is derived from the record (`extraction`,
 * `userFeedback`, `saveFeedback`). The rest — per-entry state, one request at a time,
 * dismiss / restore with sessionStorage, the subscribe / snapshot wiring — is generic. When a second
 * dismissible card needs it (e.g. asset work-order history), extract that part into a shared hook
 * keyed by feature + entity, with both features as its users, rather than copying this file.
 */
interface ResolutionSummaryEntry {
	/** `undefined` until loaded, `null` when there is nothing to show. */
	record: ResolutionKnowledgeRecord | null | undefined;
	/** Closed by the user. Per user and work order, for the rest of the browser session. */
	dismissed: boolean;
	/** The restore pill is reloading a dismissed summary. */
	restoring: boolean;
}

const NO_ENTRY: ResolutionSummaryEntry = { record: undefined, dismissed: false, restoring: false };

/**
 * Entries are keyed by the signed-in user as well as the work order: the SPA can switch users
 * without a reload, and the record carries that user's own feedback.
 */
const entryKey = (userEmail: string, workOrderId: number) => `${userEmail}|${workOrderId}`;

const entries: { [entryKey: string]: ResolutionSummaryEntry } = {};
const loading: { [entryKey: string]: boolean } = {};
const listeners = new Set<() => void>();

const dismissedStorageKey = (key: string) => `ow.resolutionSummary.dismissed.${key}`;

// Storage can be unavailable (private mode, blocked site data); dismissal then lasts for the page.
const readDismissed = (key: string): boolean => {
	try {
		return window.sessionStorage.getItem(dismissedStorageKey(key)) === 'true';
	} catch (error) {
		return false;
	}
};

const writeDismissed = (key: string, dismissed: boolean) => {
	try {
		if (dismissed) {
			window.sessionStorage.setItem(dismissedStorageKey(key), 'true');
		} else {
			window.sessionStorage.removeItem(dismissedStorageKey(key));
		}
	} catch (error) {
		// See readDismissed.
	}
};

const getEntry = (key: string): ResolutionSummaryEntry => {
	if (!entries[key]) {
		entries[key] = { ...NO_ENTRY, dismissed: readDismissed(key) };
	}
	return entries[key];
};

const updateEntry = (key: string, update: Partial<ResolutionSummaryEntry>) => {
	entries[key] = { ...getEntry(key), ...update };
	listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/**
 * One request per entry at a time, however many components ask. Resolves with the record, or
 * `undefined` when a request was already in flight.
 */
const load = (
	key: string,
	workOrderId: number,
	fetchKnowledge: FetchResolutionKnowledge
): Promise<ResolutionKnowledgeRecord | null | undefined> => {
	if (loading[key]) return Promise.resolve(undefined);
	loading[key] = true;
	return fetchKnowledge(workOrderId).then((record) => {
		loading[key] = false;
		updateEntry(key, { record, restoring: false });
		return record;
	});
};

/** Quarantined runs carry only the two summaries, so only the one-liner is required. */
const getDisplayableExtraction = (
	record: ResolutionKnowledgeRecord | null | undefined
): ResolutionKnowledgeExtraction | null => {
	const extraction = record && record.knowledge.extraction;
	return extraction && extraction.oneLinerSummary ? extraction : null;
};

interface UseResolutionSummaryOptions {
	workOrder: ResolutionSummaryWorkOrder;
	/** The signed-in user (`state.session.currentUser.email`). */
	userEmail: string;
	/** False hides the summary and skips loading it: a supplier only bidding on the work order
	 * must not see it. */
	enabled: boolean;
	fetchKnowledge: FetchResolutionKnowledge;
	saveKnowledgeFeedback?: SaveResolutionKnowledgeFeedback;
}

const RESTORE_ERROR_MESSAGE = 'Could not load the completion summary. Please try again later.';

/**
 * Loaded once per user and work order for the page session, only for completed work orders with
 * the `wo-summary` flag on. Off for subcontracted work orders: the backend keys summaries by work
 * order id, and a contractor and a subcontractor work order can share an id across their tables.
 * Restoring a dismissed summary reloads it.
 */
export const useResolutionSummary = ({
	workOrder,
	userEmail,
	enabled,
	fetchKnowledge,
	saveKnowledgeFeedback,
}: UseResolutionSummaryOptions) => {
	const workOrderId = workOrder && workOrder.id;
	const key = entryKey(userEmail, workOrderId);
	const active =
		!!workOrderId &&
		enabled &&
		!!posthog.isFeatureEnabled(WO_SUMMARY_FEATURE_FLAG) &&
		workOrder.displayStatus === 'Completed' &&
		!isSubContractedWorkOrder(workOrder) &&
		!isSubContractedFromWorkOrder(workOrder);
	const getSnapshot = () => (workOrderId ? getEntry(key) : NO_ENTRY);
	const entry = useSyncExternalStore(subscribe, getSnapshot);

	const shouldLoad = active && entry.record === undefined;
	useEffect(() => {
		if (shouldLoad) load(key, workOrderId, fetchKnowledge);
		// `fetchKnowledge` is rebuilt by mapDispatchToProps on every prop change; depending on it
		// would refire while the first request is still in flight.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, shouldLoad]);

	const dismiss = () => {
		writeDismissed(key, true);
		updateEntry(key, { dismissed: true });
	};

	const restore = () => {
		writeDismissed(key, false);
		updateEntry(key, { dismissed: false, restoring: true });
		load(key, workOrderId, fetchKnowledge).then((record) => {
			if (record === null) message.error(RESTORE_ERROR_MESSAGE);
		});
	};

	const saveFeedback = (feedback: ResolutionKnowledgeFeedback) =>
		saveKnowledgeFeedback(workOrderId, feedback).then((saved) => {
			const current = getEntry(key).record;
			if (current) updateEntry(key, { record: { ...current, userFeedback: saved } });
		});

	return {
		extraction: active ? getDisplayableExtraction(entry.record) : null,
		userFeedback: entry.record ? entry.record.userFeedback : undefined,
		dismissed: entry.dismissed,
		restoring: entry.restoring,
		dismiss,
		restore,
		saveFeedback,
	};
};
